"use client";

import { Separator } from "@/components/ui/separator";
import { GithubIcon, TwitterIcon, InstagramIcon, LinkedinIcon } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useLanguage } from "@/lib/LanguageContext";

const Footer = () => {
    const t = useTranslations("marketing.footer");
    const { language } = useLanguage();
    const isArabic = language === 'ar';

    const footerLinks = [
        { title: t('docs'), href: 'https://docs.ecommaps.com' },
        { title: t('blog') || "Blog", href: `/${language}/blog` },
        { title: t('contact') || "Contact Us", href: `/${language}/contact` },
        { title: t('pricing'), href: `/${language}/pricing` },
    ];

    return (
        <footer className="border-t">
            <div className="max-w-5xl mx-auto">
                <div className="py-12 flex flex-col justify-start items-center">
                    {/* Logo */}
                    <Link href="/" className="flex items-center gap-2">
                        {/* Light Mode Logo */}
                        <Image
                            src="/logo-light.png"
                            alt="Ecommaps Logo"
                            width={40}
                            height={40}
                            className="w-10 h-10 object-contain block dark:hidden"

                        />
                        {/* Dark Mode Logo */}
                        <Image
                            src="/logo-dark.png"
                            alt="Ecommaps Logo"
                            width={40}
                            height={40}
                            className="w-10 h-10 object-contain hidden dark:block"

                        />
                        <span className="text-2xl font-bold text-foreground tracking-tight">
                            {isArabic ? 'إيكومابس' : 'Ecommaps'}<span className="text-primary">.</span>
                        </span>
                    </Link>

                    {/* Links */}
                    <ul className="mt-6 flex items-center gap-4 flex-wrap justify-center">
                        {footerLinks.map(({ title, href }) => {
                            const isBlog = href.includes('/blog');
                            return (
                                <li key={title}>
                                    {isBlog ? (
                                        <a
                                            href={href}
                                            className="text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            {title}
                                        </a>
                                    ) : (
                                        <Link
                                            href={href}
                                            className="text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            {title}
                                        </Link>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <Separator />

                {/* Legal Links */}
                <div className="py-4 flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
                    <Link href={`/${language}/legal/terms`} className="hover:underline">{t('legal.terms')}</Link>
                    <Link href={`/${language}/legal/privacy`} className="hover:underline">{t('legal.privacy')}</Link>
                    <Link href={`/${language}/legal/merchant-agreement`} className="hover:underline">{t('legal.merchant')}</Link>
                    <Link href={`/${language}/legal/aup`} className="hover:underline">{t('legal.aup')}</Link>
                    <Link href={`/${language}/legal/refund`} className="hover:underline">{t('legal.refund')}</Link>
                </div>

                <div className="py-8 flex flex-col-reverse sm:flex-row items-center justify-between gap-x-2 gap-y-5 px-6 xl:px-0">
                    {/* Copyright */}
                    <span className="text-muted-foreground text-sm">
                        &copy; {new Date().getFullYear()}{" "}
                        <Link href="/" className="hover:text-foreground transition-colors">
                            Ecommaps
                        </Link>
                        . {t("rights")}
                    </span>

                    {/* Social Icons */}
                    <div className="flex items-center gap-5 text-muted-foreground">
                        <Link href="https://twitter.com/ecommaps" target="_blank" aria-label="Twitter" className="hover:text-foreground transition-colors">
                            <TwitterIcon className="h-5 w-5" />
                        </Link>
                        <Link href="https://instagram.com/ecommaps" target="_blank" aria-label="Instagram" className="hover:text-foreground transition-colors">
                            <InstagramIcon className="h-5 w-5" />
                        </Link>
                        <Link href="https://linkedin.com/company/ecommaps" target="_blank" aria-label="LinkedIn" className="hover:text-foreground transition-colors">
                            <LinkedinIcon className="h-5 w-5" />
                        </Link>
                        <Link href="https://github.com/ecommaps" target="_blank" aria-label="GitHub" className="hover:text-foreground transition-colors">
                            <GithubIcon className="h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
